import { TFunction } from 'i18next';
import {
  PartialProductReport,
  ReportColumnConfig,
} from 'modules/reports/types';
import { formatDateSafe } from 'modules/reports/utils/formatDateSafe';
import { formatFullName } from 'modules/reports/utils/formatFullName';
import { formatSafeValue } from 'modules/reports/utils/formatSafeValue';

type ProductReportContext = {
  categoriesMap?: Record<number, string>;
};

export const getProductReportColumns = (
  t: TFunction
): ReportColumnConfig<PartialProductReport>[] => [
  // --- Basic Info ---
  {
    key: 'id',
    label: t('id'),
    getValue: (item) => item.id,
  },
  {
    key: 'shortId',
    label: t('shortId'),
    getValue: (item) => item.shortId,
  },
  {
    key: 'createdAt',
    label: t('createdAt'),
    getValue: (item) => formatDateSafe(item.createdAt),
  },

  // --- Donation ---
  {
    key: 'donationId',
    label: t('donationId'),
    getValue: (item) => item.donation?.id || '',
  },
  {
    key: 'approvedDonationsMl',
    label: t('approvedDonationsMl'),
    getValue: (item) => formatSafeValue(item.donation?.approvedDonationsMl),
  },
  {
    key: 'actualCollectionVolumeMl',
    label: t('actualCollectionVolumeMl'),
    getValue: (item) => formatSafeValue(item.donation?.actual),
  },

  // --- Donor ---
  {
    key: 'donorFirstName',
    label: t('donorFirstName'),
    getValue: (item) => item.donation?.donor?.name || '',
  },
  {
    key: 'donorLastName',
    label: t('donorLastName'),
    getValue: (item) => item.donation?.donor?.user?.lastName || '',
  },
  {
    key: 'donorLocation',
    label: t('donorLocation'),
    getValue: (item) => item.donation?.donor?.location || '',
  },
  {
    key: 'bloodType',
    label: t('bloodType'),
    getValue: (item) => formatSafeValue(item.donation?.donor?.bloodType),
  },

  // --- Staff ---
  {
    key: 'poker',
    label: t('poker'),
    getValue: (item) => formatFullName(item.donation?.additionalInfo?.poker),
  },
  {
    key: 'primaryHolder',
    label: t('primaryHolder'),
    getValue: (item) =>
      formatFullName(item.donation?.additionalInfo?.primaryHolder),
  },

  // --- Product Details ---
  {
    key: 'anticoagulant',
    label: t('anticoagulant'),
    getValue: (item) => item.anticoagulant || '',
  },
  {
    key: 'expirationDate',
    label: t('expirationDate'),
    getValue: (item) => formatDateSafe(item.expirationDate),
  },
  {
    key: 'status',
    label: t('status'),
    getValue: (item) => item.status || '',
  },
  {
    key: 'storageRequirements',
    label: t('storageRequirements'),
    getValue: (item) => item.storageRequirements?.join(', ') || '',
  },
  {
    key: 'value',
    label: t('value'),
    getValue: (item) => item.value,
  },

  // --- Woocommerce ---
  {
    key: 'woocommerceCategoryId',
    label: t('woocommerceCategoryId'),
    getValue: (item) => item.woocommerceCategoryId,
  },
  {
    key: 'woocommerceProductId',
    label: t('woocommerceProductId'),
    getValue: (item) => item.woocommerceProductId,
  },
  {
    key: 'productCategoryName',
    label: t('productCategoryName'),
    getValue: (item, context: ProductReportContext) =>
      context?.categoriesMap?.[item.woocommerceCategoryId] || '',
  },

  // --- Respin ---
  {
    key: 'respin',
    label: t('respin'),
    getValue: (item) => {
      if (item.respin === true) return 'Yes';
      if (item.respin === false) return 'No';
      return '';
    },
  },

  // --- Region ---
  {
    key: 'productRegion',
    label: t('productRegion'),
    getValue: (item) => item.regionPreference?.name || '',
  },

  // --- Discarded Product ---
  {
    key: 'discarded',
    label: t('discarded'),
    getValue: (item) => (item.discardedProduct ? 'Yes' : 'No'),
  },
  {
    key: 'processor',
    label: t('processor'),
    getValue: (item) => formatFullName(item.discardedProduct?.processor),
  },
  {
    key: 'processorId',
    label: t('processorId'),
    getValue: (item) => item.discardedProduct?.processor?.id || '',
  },
  {
    key: 'discardedProcessingType',
    label: t('discardedProcessingType'),
    getValue: (item) => item.discardedProduct?.processingType || '',
  },
  {
    key: 'discardedReason',
    label: t('discardedReason'),
    getValue: (item) => item.discardedProduct?.reason || '',
  },
  {
    key: 'discardedNotes',
    label: t('discardedNotes', 'Discard Notes'),
    getValue: (item) => item.discardedProduct?.notes || '',
  },
];
